import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";

/**
 * Shown after analyse when an existing Sellsy contact has a phone field with a
 * value that DIFFERS from what we just extracted. Per business rule, we never
 * silently overwrite — the user explicitly decides per conflict.
 *
 * Props:
 *   open: bool
 *   contactName: string
 *   conflicts: Array<{ field, sellsy_field, existing, new }>
 *   onConfirm: (overwriteFieldList: string[]) => void   // user accepted some
 *   onCancel: () => void                                // user kept everything
 */
const PhoneConflictDialog = ({ open, contactName, conflicts, onConfirm, onCancel }) => {
  const labels = {
    mobile_phone: "Numéro portable",
    landline_phone: "Numéro fixe",
  };

  const handleReplaceAll = () => {
    onConfirm(conflicts.map((c) => c.field));
  };

  const handleReplaceOne = (field) => {
    onConfirm([field]);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onCancel(); }}>
      <DialogContent className="max-w-md" data-testid="phone-conflict-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-700">
            <AlertTriangle className="w-5 h-5" />
            Numéro de téléphone différent détecté
          </DialogTitle>
          <DialogDescription>
            Le contact <strong>{contactName}</strong> existe déjà dans Sellsy avec des coordonnées qui
            diffèrent de celles trouvées dans l'email. Voulez-vous conserver les valeurs actuelles ou
            les remplacer ?
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {conflicts.map((c) => (
            <div key={c.field} className="border border-slate-200 rounded-md p-3 bg-slate-50" data-testid={`conflict-row-${c.field}`}>
              <p className="text-xs font-medium text-slate-600 mb-2">
                {labels[c.field] || c.field}
              </p>
              <div className="grid grid-cols-1 gap-1.5 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-slate-500 text-xs">Sellsy (actuel)</span>
                  <code className="font-mono text-slate-800 text-xs bg-white px-2 py-0.5 rounded border border-slate-200">
                    {c.existing}
                  </code>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-emerald-600 text-xs">Email (nouveau)</span>
                  <code className="font-mono text-emerald-700 text-xs bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                    {c.new}
                  </code>
                </div>
              </div>
              <div className="mt-2 flex justify-end">
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs h-7"
                  onClick={() => handleReplaceOne(c.field)}
                  data-testid={`conflict-replace-${c.field}`}
                >
                  Remplacer ce numéro
                </Button>
              </div>
            </div>
          ))}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={onCancel}
            data-testid="conflict-keep-all-btn"
          >
            Conserver les valeurs Sellsy
          </Button>
          {conflicts.length > 1 && (
            <Button
              onClick={handleReplaceAll}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="conflict-replace-all-btn"
            >
              Tout remplacer ({conflicts.length})
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PhoneConflictDialog;
